import type { QueryClient } from "@tanstack/react-query";
import { prefetchChat } from "@/features/chat/chatSubscriptions";
import { likelyChatId } from "@/features/chat/chatRecall";
import { perfMark } from "./perf";
import { qk } from "./query-keys";
import { fetchWorktreeDetail } from "./worktree-query";

/**
 * Intent prefetch: a pointer resting on a task row (or keyboard focus on it)
 * loads the task's detail and the newest turns of the chat it would open on,
 * so the click paints from memory. Router `intent` preloading only loads code.
 */

const inflight = new Map<string, Promise<void>>();
/** A task's detail is fresh enough to reuse for this long. */
const DETAIL_FRESH_MS = 15_000;

export function prefetchWorktree(queryClient: QueryClient, worktreeId: string): Promise<void> {
  const existing = inflight.get(worktreeId);
  if (existing) return existing;
  perfMark(`rivets:prefetch:${worktreeId}`);
  const run = (async () => {
    const detail = await queryClient.fetchQuery({
      queryKey: qk.worktree(worktreeId),
      queryFn: ({ signal }) => fetchWorktreeDetail(queryClient, worktreeId, signal),
      staleTime: DETAIL_FRESH_MS,
    });
    const chatId = likelyChatId(detail.chats, worktreeId);
    if (chatId) await prefetchChat(chatId);
  })().catch(() => undefined).finally(() => inflight.delete(worktreeId));
  inflight.set(worktreeId, run);
  return run;
}

/** Hover intent: only a pointer that stays this long prefetches, so sweeping
 * across the sidebar does not fetch every row it crosses. */
export const PREFETCH_INTENT_MS = 60;

/** Pending hover timers by worktree, module-level so a re-render between
 * enter and leave still cancels the right one. */
const intentTimers = new Map<string, ReturnType<typeof setTimeout>>();

type IntentHandlers = { onPointerEnter: () => void; onPointerLeave: () => void; onFocus: () => void; onBlur: () => void };
/** One handler set per worktree, so a row's props are the same objects on
 * every render. (The query client is the app's one instance.) */
const handlersByWorktree = new Map<string, IntentHandlers>();

export function intentPrefetchHandlers(queryClient: QueryClient, worktreeId: string): IntentHandlers {
  const existing = handlersByWorktree.get(worktreeId);
  if (existing) return existing;
  const cancel = () => {
    const timer = intentTimers.get(worktreeId);
    if (timer) clearTimeout(timer);
    intentTimers.delete(worktreeId);
  };
  // Pointer rest and keyboard focus are the same intent, with the same delay:
  // arrowing down a long sidebar must not fetch every row it passes.
  const arm = () => {
    cancel();
    intentTimers.set(worktreeId, setTimeout(() => {
      intentTimers.delete(worktreeId);
      void prefetchWorktree(queryClient, worktreeId);
    }, PREFETCH_INTENT_MS));
  };
  const handlers = { onPointerEnter: arm, onPointerLeave: cancel, onFocus: arm, onBlur: cancel };
  handlersByWorktree.set(worktreeId, handlers);
  return handlers;
}
